import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Divider,
  IconButton,
  InputAdornment,
  Chip,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  AttachMoney as AttachMoneyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { teacherService } from '../../services/api';

const schema = yup.object({
  first_name: yup.string().required('Nombre es requerido'),
  last_name: yup.string().required('Apellido es requerido'),
  email: yup.string().email('Email inválido').required('Email es requerido'),
  phone: yup.string().nullable(),
  hourly_rate: yup.number()
    .required('Tarifa por hora es requerida')
    .min(0, 'La tarifa no puede ser negativa')
    .typeError('Debe ser un número válido'),
  status: yup.string().oneOf(['active', 'inactive']).default('active'),
  specialization: yup.string().nullable()
});

const specializations = [
  'Matemáticas',
  'Lengua y Literatura',
  'Historia',
  'Ciencias Naturales',
  'Física',
  'Química',
  'Biología',
  'Inglés',
  'Ruso',
  'Educación Física',
  'Arte',
  'Música',
  'Informática',
  'Otra'
];

const TeacherForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  //const { user } = useSelector((state: RootState) => state.auth);
  
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      hourly_rate: 500,
      status: 'active',
      specialization: ''
    }
  });

  const watchFirstName = watch('first_name');
  const watchLastName = watch('last_name');
  const watchHourlyRate = watch('hourly_rate');

  useEffect(() => {
    if (isEdit) {
      loadTeacher();
    }
  }, [id]);

  const loadTeacher = async () => {
    try {
      setLoading(true);
      const response = await teacherService.getById(parseInt(id!));
      reset(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar profesor');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);

      if (isEdit) {
        await teacherService.update(parseInt(id!), data);
      } else {
        await teacherService.create(data);
      }

      setSuccess(true);
      
      setTimeout(() => {
        navigate('/teachers');
      }, 2000);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar profesor');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Información Personal', 'Datos Profesionales', 'Confirmación'];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton component={Link} to="/teachers" sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Editar Profesor' : 'Nuevo Profesor'}
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Profesor guardado exitosamente! Redirigiendo...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Step 1: Información Personal */}
          {activeStep === 0 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Información Personal
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                          name="first_name"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Nombre *"
                              error={!!errors.first_name}
                              helperText={errors.first_name?.message}
                              disabled={loading}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon />
                                  </InputAdornment>
                                )
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                          name="last_name"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Apellidos *"
                              error={!!errors.last_name}
                              helperText={errors.last_name?.message}
                              disabled={loading}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PersonIcon />
                                  </InputAdornment>
                                )
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                    {(watchFirstName || watchLastName) && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Nombre completo:</strong> {watchFirstName} {watchLastName}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Información de Contacto
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                          name="email"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Email *"
                              type="email"
                              error={!!errors.email}
                              helperText={errors.email?.message}
                              disabled={loading}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <EmailIcon />
                                  </InputAdornment>
                                )
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                          name="phone"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Teléfono"
                              placeholder="Ej: +7 123 456 7890"
                              error={!!errors.phone}
                              helperText={errors.phone?.message}
                              disabled={loading}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <PhoneIcon />
                                  </InputAdornment>
                                )
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {/* Step 2: Datos Profesionales */}
          {activeStep === 1 && (
            <>
              <Grid size={{ xs: 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Datos Profesionales
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                          name="specialization"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              label="Especialización"
                              error={!!errors.specialization}
                              helperText={errors.specialization?.message}
                              disabled={loading}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SchoolIcon />
                                  </InputAdornment>
                                )
                              }}
                            >
                              <MenuItem value="">Seleccionar especialización</MenuItem>
                              {specializations.map((spec) => (
                                <MenuItem key={spec} value={spec}>
                                  {spec}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />
                      </Grid>
                      
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Controller
                          name="hourly_rate"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              type="number"
                              label="Tarifa por Hora (RUB) *"
                              error={!!errors.hourly_rate}
                              helperText={errors.hourly_rate?.message}
                              disabled={loading}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <AttachMoneyIcon />
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <Chip 
                                      label="por hora" 
                                      size="small" 
                                      variant="outlined" 
                                    />
                                  </InputAdornment>
                                )
                              }}
                            />
                          )}
                        />
                      </Grid>
                      
                      <Grid size={{ xs: 12 }}>
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={field.value === 'active'}
                                  onChange={(e) => field.onChange(e.target.checked ? 'active' : 'inactive')}
                                  disabled={loading}
                                  color="success"
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body1">
                                    {field.value === 'active' ? 'Activo' : 'Inactivo'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {field.value === 'active' 
                                      ? 'El profesor puede recibir pagos y dar clases'
                                      : 'El profesor no está disponible actualmente'
                                    }
                                  </Typography>
                                </Box>
                              }
                              labelPlacement="end"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                    { watchHourlyRate  === 500 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        La tarifa por hora predeterminada es de 500 RUB. Puedes ajustarla según sea necesario.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {/* Step 3: Confirmación */}
          {activeStep === 2 && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Confirmar Información
                  </Typography>
                  
                  <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Nombre Completo
                        </Typography>
                        <Typography variant="body1">
                          {watchFirstName} {watchLastName}
                        </Typography>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body1">
                          {watch('email')}
                        </Typography>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Teléfono
                        </Typography>
                        <Typography variant="body1">
                          {watch('phone') || 'No especificado'}
                        </Typography>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Especialización
                        </Typography>
                        <Typography variant="body1">
                          {watch('specialization') || 'No especificada'}
                        </Typography>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Tarifa por Hora
                        </Typography>
                        <Typography variant="h6" color="primary">
                          ₽{watch('hourly_rate')}
                        </Typography>
                      </Grid>
                      
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Estado
                        </Typography>
                        <Chip
                          label={watch('status') === 'active' ? 'Activo' : 'Inactivo'}
                          color={watch('status') === 'active' ? 'success' : 'default'}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Navigation Buttons */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="space-between">
              <Button
                onClick={handleBack}
                disabled={activeStep === 0 || loading}
              >
                Anterior
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Guardar'}
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                >
                  Siguiente
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default TeacherForm;